import { useState, useEffect, useCallback, useRef } from 'react'; // Added useRef
import { useNavigate } from 'react-router-dom';
import { useFirebaseAuth } from './useFirebaseAuth'; // Assumed to provide Firebase user and auth state
import { getFirestore, doc, getDoc, setDoc } from 'firebase/firestore'; // Import Firestore functions
import { getAuth } from 'firebase/auth'; // Import getAuth to directly access auth instance for refresh

// IMPORTANT: Ensure 'db' and 'firebaseAuthInstance' are correctly initialized and available.
// This assumes your main firebase.js file initializes the app and exports them.
// Your firebase.js (which you showed me) now correctly exports db.
// So this part is fine.
const db = getFirestore(); // Assumes default Firebase app is initialized or will be done by firebase.js
const firebaseAuthInstance = getAuth(); // Get the auth instance to force token refresh

// Ensure this environment variable is set in your .env.local or build process
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:3001';

/**
 * Secure Admin Authentication Hook
 * Enforces strict admin-only access with multiple validation layers
 */
// ADMIN SECURITY CONFIGURATION - STRICT WHITELIST ONLY
const ADMIN_CONFIG = {
    // Backend verification via custom claims is REQUIRED.
    // This is implicitly handled by checking Firebase custom claims (set by your backend using Admin SDK).
    // No additional frontend whitelist checks.
    requireBackendVerification: true,

    // Session timeout for admin users (30 minutes) - this is for frontend display/soft-logout
    // The *real* Firebase ID token expiry is 1 hour by default, enforced by Firebase.
    sessionTimeout: 30 * 60 * 1000, // 30 minutes in milliseconds

    // Enable audit logging for admin actions (if you have frontend logging)
    enableAuditLogging: true,

    // IMPORTANT: Set to true ONLY in development, MUST be false in production
    // This bypass should *only* be used for local development when backend (for custom claims) is down/unreachable
    // or if you haven't set up custom claims yet. In production, an error from backend should ALWAYS lead to denial.
    allowDevBackendBypass: process.env.NODE_ENV !== 'production', // Simple check for development
};

export const useSecureAdminAuth = (options = {}) => {
    const [adminUser, setAdminUser] = useState(null);
    const [isAdminLoading, setIsAdminLoading] = useState(true);
    const [adminError, setAdminError] = useState(null);
    const [clientSessionExpiry, setClientSessionExpiry] = useState(null);
    const [isAdmin, setIsAdmin] = useState(false);

    // Ref to manage setTimeout for navigation, preventing multiple redirects
    const navigateTimerRef = useRef(null);

    const navigate = useNavigate();

    // useFirebaseAuth hook provides Firebase user, loading state, and signOut function
    const { user, loading, signOut } = useFirebaseAuth({
        context: 'admin',
        ...options
    });

    // Function to verify admin status based on Firebase Custom Claims
    const checkFirebaseAdminClaims = useCallback(async (firebaseUser) => {
        try {
            // Force token refresh to ensure we have the latest claims.
            // This is crucial if admin claims are set AFTER the user initially signs in.
            const idTokenResult = await firebaseUser.getIdTokenResult(true); // Forces refresh
            const claims = idTokenResult.claims || {};
            // Claims check for admin privileges

            if (claims.admin === true) {
                return { isAdmin: true };
            } else {
                return { isAdmin: false, error: 'Access denied: User does not have required admin claim.' };
            }
        } catch (error) {
            console.error('Error during Firebase custom claim check:', error);
            // In dev mode, allow bypass if config permits AND there's a specific network error
            if (ADMIN_CONFIG.allowDevBackendBypass && (error.code === 'auth/network-request-failed' || error.message.includes('network error'))) {
                console.warn('DEBUG: Allowing admin access due to dev backend bypass and network error.');
                return { isAdmin: true, bypass: true }; // Allow access for dev purposes
            }
            return { isAdmin: false, error: `Failed to verify admin claim: ${error.message}` };
        }
    }, []); // No dependencies as it uses firebaseUser passed in

    // Firestore specific logic - Create profile if not exists, get role
    const ensureFirestoreProfileAndGetRole = useCallback(async (firebaseUser) => {
        const userDocRef = doc(db, 'users', firebaseUser.uid);
        try {
            const docSnap = await getDoc(userDocRef);

            if (docSnap.exists()) {
                const userData = docSnap.data();
                return userData.role;
            } else {
                console.log("DEBUG: No Firestore profile found for", firebaseUser.uid, ". Creating default profile.");
                const defaultUserData = {
                    email: firebaseUser.email,
                    displayName: firebaseUser.displayName || firebaseUser.email, // Use email if no display name
                    photoURL: firebaseUser.photoURL || null,
                    role: 'user', // Default role for new users
                    createdAt: new Date(),
                    lastLogin: new Date(),
                };
                await setDoc(userDocRef, defaultUserData);
                return 'user';
            }
        } catch (error) {
            console.error("Error accessing/creating Firestore user profile:", error);
            // Return 'user' role on error to avoid breaking the app, but log the error
            return 'user';
        }
    }, []); // No dependencies as it uses firebaseUser passed in

    // Main effect to validate admin access
    useEffect(() => {
        const checkAdminAccess = async () => {
            console.log('DEBUG: checkAdminAccess initiated. User:', user?.email, 'Loading:', loading, 'Current isAdmin state:', isAdmin);

            // 1. Handle loading state from useFirebaseAuth
            if (loading) {
                setIsAdminLoading(true); // Keep loading state true while auth state is resolving
                return;
            }

            // 2. Handle no authenticated user
            if (!user) {
                setAdminUser(null);
                setIsAdmin(false);
                setClientSessionExpiry(null);
                setAdminError('No authenticated user. Please sign in.');
                setIsAdminLoading(false);
                // Only navigate if not already on root and no pending redirect
                if (window.location.pathname !== '/' && !navigateTimerRef.current) {
                    console.log('DEBUG: Redirecting to root in 3 seconds due to no user.');
                    navigateTimerRef.current = setTimeout(() => {
                        navigate('/');
                        navigateTimerRef.current = null; // Clear the ref after navigation
                    }, 3000);
                }
                return;
            }

            // 3. Skip re-checking if already admin and session is valid
            // This prevents continuous re-validation if the user is already confirmed admin and session is active
            if (isAdmin && user.uid === adminUser?.uid && clientSessionExpiry && clientSessionExpiry > Date.now()) {
                console.log('DEBUG: User already confirmed admin and client session is valid. Skipping full check.');
                setIsAdminLoading(false);
                return;
            }

            // 4. Perform full admin verification
            setIsAdminLoading(true);
            setAdminError(null); // Clear previous errors
            try {
                // Ensure Firestore profile exists and get role (for logging/secondary checks)
                const firestoreRole = await ensureFirestoreProfileAndGetRole(user);
                console.log(`DEBUG: Firestore reported role for ${user.email}: ${firestoreRole}`);

                // Crucial step: Verify admin claim
                const verificationResult = await checkFirebaseAdminClaims(user);
                console.log('DEBUG: Firebase Custom Claims verification result:', verificationResult);

                if (verificationResult.isAdmin) {
                    setAdminUser(user);
                    setIsAdmin(true);
                    setAdminError(null);
                    // Only set session expiry if not set or expired
                    if (!clientSessionExpiry || clientSessionExpiry < Date.now()) {
                        setClientSessionExpiry(Date.now() + ADMIN_CONFIG.sessionTimeout);
                    }

                    // If we're on a non-admin path but should be on admin, navigate
                    if (!window.location.pathname.startsWith('/admin')) {
                        console.log('DEBUG: Admin access granted, navigating to /admin.');
                        navigate('/admin');
                    }
                } else {
                    // Access denied based on claims
                    console.log(`DEBUG: 🚫 Admin access DENIED for ${user.email}. Reason: ${verificationResult.error}`);
                    setAdminUser(null);
                    setIsAdmin(false);
                    setClientSessionExpiry(null);
                    setAdminError(verificationResult.error || 'Access denied. You do not have administrator privileges.');
                    await signOut(); // Force sign out if access denied
                    // Immediate redirect for denied access
                    if (window.location.pathname !== '/' && !navigateTimerRef.current) {
                         console.log('DEBUG: Redirecting to root immediately due to denied access.');
                         navigate('/');
                    }
                }
            } catch (err) {
                console.error('DEBUG: Critical error during admin access check:', err);
                setAdminUser(null);
                setIsAdmin(false);
                setClientSessionExpiry(null);
                setAdminError(err.message || 'An unexpected error occurred during admin verification. Please try again.');
                await signOut(); // Sign out on severe error
                // Redirect with delay on critical error
                if (window.location.pathname !== '/' && !navigateTimerRef.current) {
                    console.log('DEBUG: Redirecting to root in 3 seconds due to critical error.');
                    navigateTimerRef.current = setTimeout(() => {
                        navigate('/');
                        navigateTimerRef.current = null;
                    }, 3000);
                }
            } finally {
                setIsAdminLoading(false);
                console.log('DEBUG: checkAdminAccess finished.');
            }
        };

        checkAdminAccess();

        // Cleanup function for useEffect: clear any pending timeouts when the component unmounts
        // or if dependencies change and the effect re-runs.
        return () => {
            if (navigateTimerRef.current) {
                clearTimeout(navigateTimerRef.current);
                navigateTimerRef.current = null;
            }
        };
        // Added isAdmin, adminUser, clientSessionExpiry to dependencies for more robust re-evaluation
    }, [user, loading, navigate, signOut, checkFirebaseAdminClaims, ensureFirestoreProfileAndGetRole, isAdmin, adminUser, clientSessionExpiry]);

    // Effect for client-side session timeout (independent of the main check)
    useEffect(() => {
        let timer;
        if (isAdmin && clientSessionExpiry) {
            const timeRemaining = clientSessionExpiry - Date.now();
            if (timeRemaining > 0) {
                console.log(`DEBUG: Admin session active. Timeout in ${Math.round(timeRemaining / 1000)} seconds.`);
                timer = setTimeout(() => {
                    console.warn('DEBUG: Admin client-side session expired. Forcing frontend sign out.');
                    // Use the passed signOut from useFirebaseAuth directly
                    signOut();
                    setAdminUser(null);
                    setClientSessionExpiry(null);
                    setIsAdmin(false);
                    setAdminError('Admin session expired. Please re-authenticate.');
                    navigate('/'); // Redirect after logout
                }, timeRemaining);
            } else {
                console.warn('DEBUG: Admin client-side session already expired on load. Forcing logout.');
                signOut();
                setAdminUser(null);
                setClientSessionExpiry(null);
                setIsAdmin(false);
                setAdminError('Admin session expired. Please re-authenticate.');
                navigate('/');
            }
        }
        // Cleanup function for this specific timer
        return () => {
            if (timer) clearTimeout(timer);
        };
    }, [isAdmin, clientSessionExpiry, signOut, navigate]); // Dependencies for this specific timer


    // Exposed function for admin-specific sign out
    const handleAdminSignOut = useCallback(async () => {
        try {
            await signOut();
            setAdminUser(null);
            setClientSessionExpiry(null);
            setIsAdmin(false);
            setAdminError(null);
            console.log('DEBUG: Admin user explicitly signed out.');
            navigate('/');
        } catch (error) {
            console.error('Admin logout error:', error);
        }
    }, [signOut, navigate]);


    return {
        adminUser,
        isAdminLoading,
        adminError,
        clientSessionExpiry,
        adminSignOut: handleAdminSignOut,
        isAdmin,
        timeUntilExpiry: clientSessionExpiry ? Math.max(0, clientSessionExpiry - Date.now()) : 0
    };
};

export default useSecureAdminAuth;